import {Directive, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {FormControl} from '@angular/forms';
import {Sort} from '@angular/material/sort';
import {DateUtil} from '../../util/DateUtil';
import {TableControl} from '../../models/TableControl';
import {SmsDTO} from '../../../pbm/models/sms/SmsDTO';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {SmsService} from '../../../pbm/services/sms.service';
import {SharedService} from '../../services/shared-service';
import {ClmRequestBuilder} from '../../../clm/services/clm-request-builder';

enum TableColumns {
  Icons = 'icons',
  SmsId = 'smsId',
  IsSent = 'isSent',
  ToNumber = 'toNumber',
  Message = 'message',
  CommunicationTriggerType = 'communicationTriggerType',
  TriggerId = 'triggerId',
  SmsTemplate = 'smsTemplate',
}

@Directive()
export abstract class BaseSmsClassDirective implements OnInit, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly controls = ClmRequestBuilder.getTableControls();
  readonly tableColumns = TableColumns;

  loggedInUserName: string;
  smsOverrideFormControl = new FormControl('');
  smsTableControl: TableControl;
  smsExpandedRow: SmsDTO | null;
  smsDisplayedColumns: string[] = Object.values(this.tableColumns);
  smsDataSource: MatTableDataSource<SmsDTO> = new MatTableDataSource();

  @ViewChild('smsPaginator', {static: false}) smsPaginator: MatPaginator;

  // Abstract methods for child class to implement
  protected abstract getListOfSMS(tableControl: TableControl): void;

  protected get isDataExist(): boolean {
    return !!this.smsDataSource?.data?.length;
  }

  protected constructor(
    protected smsService: SmsService,
    protected sharedService: SharedService
  ) {
    this.smsTableControl = new TableControl(
      this.controls.length,
      this.controls.pageSize,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      'smsId',
      'desc',
      '',
      '');
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getListOfSMS(this.smsTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }

    if (this.smsPaginator) {
      this.smsPaginator.firstPage();
      this.smsTableControl.pageNumber = 0;
    }

    this.smsTableControl.sortField = this.getSortField($event.active);
    this.smsTableControl.sortDirection = $event.direction;
    this.getListOfSMS(this.smsTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.smsTableControl.pageNumber = $event.pageIndex;
    this.smsTableControl.pageSize = $event.pageSize;
    this.getListOfSMS(this.smsTableControl);
  }

  onRowClick(event: MouseEvent, row: SmsDTO): void {
    const targetElement = event.target as HTMLElement;
    if (targetElement.classList.contains('mat-checkbox-inner-container')) {
      event.preventDefault();
      event.stopPropagation();
      row.sendSmsChecked = !row.sendSmsChecked;
    }
    else {
      this.smsExpandedRow = this.smsExpandedRow === row ? null : row;
    }
  }

  onClickSendSmsConfirmed(event: MouseEvent, row: SmsDTO): void {
    event.preventDefault();
    event.stopPropagation();
    this.sendSms(row);
  }

  onClickToggleIcons(event: MouseEvent, row: SmsDTO): void {
    event.preventDefault();
    event.stopPropagation();
    row.sendSmsChecked = !row.sendSmsChecked;
  }

  onClickEditNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();
    row.isEditNumber = true;
    this.smsOverrideFormControl.patchValue(row.editedToNumber || row.toNumber);
  }

  onClickCancelEditNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();
    row.isEditNumber = false;
    row.editedToNumber = '';
    this.smsOverrideFormControl.patchValue('');
    this.smsOverrideFormControl.updateValueAndValidity();
  }

  onClickSaveNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();
    if (!this.smsOverrideFormControl.value) {
      this.sharedService.openSnackBar('Provide valid phone number', 'Dismiss');
      return;
    }
    row.isEditNumber = false;
    row.editedToNumber = this.smsOverrideFormControl.value;
  }

  OnClickDuplicate(duplicate: boolean, row: SmsDTO): void {
    if (duplicate) {
      const newSMS: SmsDTO = {
        ...this.getSmsDTOPayload(row),
        smsId: null,
        isSent: 0,
        updated: null,
        updatedBy: null,
        created: new Date(),
        createdBy: this.loggedInUserName,
      };
      this.smsDataSource.data = [newSMS, ...this.smsDataSource.data];
    }
  }

  private sendSms(row: SmsDTO): void {
    const smsDTO: SmsDTO = row.smsId !== null ? this.getSmsDTOPayload(row) : row;
    smsDTO.toNumber = row.editedToNumber || row.toNumber;
    smsDTO.toNumberManualOverride = true; // Front-end sent emails must override the toAddress always

    row.smsSentBusyCounter++;
    this.smsService.sendSms(smsDTO)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.smsSentBusyCounter--;
        })
      )
      .subscribe({
        next: () => {
          this.sharedService.openSnackBar('SMS sent successfully!', 'Dismiss');
          this.getListOfSMS(this.smsTableControl);
        },
        error: (errorResponse) => {
          this.sharedService.openSnackBar(errorResponse.error?.businessMessage, 'Dismiss');
        }
      });
  }

  private getSmsDTOPayload(smsDTO: SmsDTO): SmsDTO {
    return {
      ...smsDTO,
      updated: new Date(),
      updatedBy: this.loggedInUserName,
    } as SmsDTO;
  }

  private getSortField(active: string): string {
    switch (active) {
    case this.tableColumns.CommunicationTriggerType:
      return 'communicationTriggerTypeByCommunicationTriggerTypeId.name';
    case this.tableColumns.SmsTemplate:
      return 'smsTemplateBySmsTemplateId.processingClassByProcessingClassId.className';
    default:
      return active;
    }
  }
}
